import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { SettingsService } from '../settings/settings.service'
import { StripeSettingsDialogComponent, StripeSettingsDialogData } from './stripe-settings-dialog/stripe-settings-dialog.component'
import { StripeConnectOnboardingComponent } from './stripe-connect/stripe-connect-onboarding.component'
import { StripeConnectStatusComponent } from './stripe-connect/stripe-connect-status.component'
import { LoginService } from 'src/app/login/login.service'
import { CreateConnectAccountInput, stripeConnectRequiredFields } from '../settings/settings.service'

interface PaymentMethod {
  id: string
  description: string
  title: string
  checked: boolean
  paymentId?: string
  category?: string
}

interface Settings {
  paymentMethod?: PaymentMethod[]
  stripApiKey?: string
  stripePublishableKey?: string
  stripeWebhookSecret?: string
  geidea?: {
    publicKey: string
    apiPassword: string
    enCodedGeideaAuth?: string
  }
  paymob?: {
    publicKey: string
    secretKey: string
    country?:string
    baseUrl?:string
  }
  paypbal?:{
    clientId:string
    secretKey:string
    paypalToken?:string
  }
  dineropay?: {
    merchantKey: string
    merchantPassword: string
  }
  nomod?: {
    apiKey: string
  }
  [key: string]: any
}

@Component({
  selector: 'app-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrl: './payment-methods.component.scss'
})
export class PaymentMethodsComponent implements OnInit {
  settings: Settings = {}
  settingsProgress = false
  saveState = false
  formSubmitted = false
  paymentData: PaymentMethod[] = []
  paymobSubMethods: PaymentMethod[] = []
  isStripChecked = false
  isGeideaChecked = false
  isPaypalChecked = false
  isCodChecked = false
  isPaymobChecked = false
  isPaymobMigsChecked = false
  isPaymobWalletChecked = false
  isPaymobPaypalChecked = false
  isPaymobKioskChecked = false
  isValidGeidea = true
  // validate stripe (BYOK mode only — Connect mode doesn't use a raw API key)
  isValidStripeApiKey = true
  invalidStripeApiKey = ''
  invalidGeideaKey = ''
  // validate paypal
  isValidPaypal = true
  invalidPaypalKey = ''
  // dineropay
  isDineroPayChecked = false
  // nomod
  isNomodChecked = false

  /**
   * Live status from project_stripe_connect_accounts (via getStripeConnectStatus),
   * not the possibly-stale `checked` flag saved in commerce_settings — an account
   * can be disabled/restricted after the fact (Stripe's own risk review, a bank
   * account getting removed, etc.), synced into that table by the Connect webhook
   * independently of whatever this page last saved. null until loaded.
   */
  stripeConnectStatus: {
    onboardingStatus: string
    transfersCapabilityStatus: string | null
    currentlyDue: string[]
    pastDue: string[]
    eventuallyDue: string[]
    currentDeadline: string | null
    // Active but Stripe will pause payouts unless eventuallyDue is provided (e.g.
    // company registration number once volume passes a threshold).
    payoutsAtRisk: boolean
  } | null = null

  paymentSettingsForm: FormGroup

  paymentMethods: PaymentMethod[] = [
    { id: 'cod', description: 'Cash On Delivery', title: 'Cash On Delivery', checked: true },
    { id: 'stripe', description: 'Pay With Card', title: 'Stripe', checked: false },
    { id: 'paypal', description: 'Pay With Card', title: 'Paypal', checked: false },
    { id: 'geidea', description: 'Pay With Card', title: 'Geidea', checked: false },
    { id: 'dineropay', description: 'Pay With Card', title: 'DineroPay', checked: false },
    { id: 'nomod', description: 'Pay With Cards, wallets, or pay-later options', title: 'Nomod', checked: false },
    { id: 'paymob', description: 'Paymob', title: 'Paymob', checked: false, category: 'paymob' },
    { id: 'paymob-migs', description: 'Pay With Card', title: 'Debit-Credit Card', paymentId: '', category: 'paymob', checked: false },
    { id: 'paymob-wallet', description: 'Pay With Wallet', title: 'Mobile Wallet', paymentId: '', category: 'paymob', checked: false },
    { id: 'paymob-paypal', description: 'Pay With Paypal', title: 'Paypal', paymentId: '', category: 'paymob', checked: false },
    { id: 'paymob-kiosk', description: 'Pay With Kiosk', title: 'Kiosk', paymentId: '', category: 'paymob', checked: false },
    { id: 'paymob-applePay', description: 'Pay With Apple Pay', title: 'Apple Pay', paymentId: '', category: 'paymob', checked: false }
  ]

  paymobCountries = [
    { code: 'EG', name: 'Egypt', api: 'https://accept.paymob.com/' },
    { code: 'AE', name: 'United Arab Emirates', api: 'https://uae.paymob.com/' },
    { code: 'SA', name: 'Saudi Arabia', api: 'https://ksa.paymob.com/' },
    { code: 'OM', name: 'Oman', api: 'https://oman.paymob.com/' },
    { code: 'PK', name: 'Pakistan', api: 'https://pakistan.paymob.com/' }
  ]

  companyCountry = 'EG'

  private readonly countrySupport: Record<string, string[]> = {
    geidea:    ['EG', 'SA', 'AE', 'JO', 'KW', 'BH', 'QA', 'OM'],
    paymob:    ['EG', 'AE', 'SA', 'OM', 'PK'],
    dineropay: ['SA'],
    nomod:     ['AE'],
    // stripe and paypal are global — empty means all countries
    stripe: [],
    paypal: [],
    cod:    [],
  }

  isMethodAvailableInCountry(methodId: string): boolean {
    const baseId = methodId.startsWith('paymob-') ? 'paymob' : methodId
    const supported = this.countrySupport[baseId]
    if (!supported) return true           // unknown provider → show it
    if (supported.length === 0) return true  // global provider
    return supported.includes(this.companyCountry.toUpperCase())
  }
  
  constructor(private settingService: SettingsService, private fb: FormBuilder, private dialog: MatDialog, private loginService: LoginService) {
    this.paymentSettingsForm = this.fb.group({
      stripApiKey: [''],
      stripePublishableKey: [''],
      stripeWebhookSecret: [''],
      geideaPublicKey: [''],
      geideaApiPassword: [''],
      paypalClientId: [''],
      paypalSecretKey: [''],
      paymobMigsPaymentId: [''],
      paymobWalletPaymentId: [''],
      paymobPaypalPaymentId: [''],
      paymobKioskPaymentId: [''],
      paymobPublicKey: [''],
      paymobSecretKey: [''],
      paymobCountry: [''] ,
      paymobApplePaymentId:[''],
      dineropayMerchantKey: [''],
      dineropayMerchantPassword: [''],
      nomodApiKey: ['']
    })
    this.updatePaymobSubMethods()
  }

  ngOnInit(): void {
    this.loadPaymentSettings()
    this.loadStripeConnectStatus()
  }

  /**
   * Reads the project's real Stripe Connect status straight from
   * project_stripe_connect_accounts on page load — not derived from the saved
   * `paymentMethod[].checked` flag, which only reflects what this page last
   * persisted and can drift from reality (see stripeConnectStatus's doc comment).
   * Silently leaves stripeConnectStatus null on error/no-account-yet — the
   * "Not connected" badge is the correct default in that case, not an alert.
   */
  private loadStripeConnectStatus(): void {
    this.settingService.getStripeConnectStatus().subscribe({
      next: (status: any) => {
        this.stripeConnectStatus = status
        // Reconciles the card's visual "active" state (driven by payment.checked)
        // with reality — e.g. an account onboarded before this page's checked flag
        // existed, or completed straight through Stripe's dashboard, would otherwise
        // show "Connected" in the badge but not look active. Silent: this is a
        // background sync on page load, not a user-triggered action worth an alert.
        const stripeMethod = this.paymentMethods.find((m) => m.id === 'stripe')
        if (stripeMethod) this.syncStripeCheckedState(stripeMethod)
      },
      error: (err) => console.error('Failed to load Stripe Connect status', err)
    })
  }

  /** Marks Stripe checked/active + persists it, only if it isn't already and is genuinely usable. No-op otherwise. */
  private syncStripeCheckedState(payment: PaymentMethod): void {
    if (!this.isStripeConnectFullyActive || payment.checked) return

    payment.checked = true
    this.updatePaymentState(payment, true)
    this.toggleValidators(payment, true)
    this.updatePaymobSubMethods()
    this.settingService.submitSettings({ paymentMethod: this.paymentData }).subscribe({
      error: (err: any) => console.error('Failed to save Stripe as an enabled payment method', err)
    })
  }

  /** Whether Stripe is genuinely usable at checkout right now — same condition finalizeStripeConnectStatus gates on. */
  get isStripeConnectFullyActive(): boolean {
    const status = this.stripeConnectStatus
    return status?.transfersCapabilityStatus === 'active'
      && (status?.currentlyDue?.length ?? 0) === 0
      && (status?.pastDue?.length ?? 0) === 0
  }


  private loadPaymentSettings(): void {
    this.settingService.getSettings().subscribe((settings: Settings) => {
      const effectiveSettings = settings || {}
      this.settings = effectiveSettings
      this.companyCountry = (effectiveSettings.companyCountry || 'EG').toUpperCase()

      this.paymentSettingsForm.patchValue({
        stripApiKey: effectiveSettings.stripApiKey || '',
        stripePublishableKey: effectiveSettings.stripePublishableKey || '',
        stripeWebhookSecret: effectiveSettings.stripeWebhookSecret || '',
        geideaPublicKey: effectiveSettings.geidea?.publicKey || '',
        geideaApiPassword: effectiveSettings.geidea?.apiPassword || '',
        paypalClientId :effectiveSettings.paypal?.clientId || '',
        paypalSecretKey :effectiveSettings.paypal?.secretKey || '',
        paymobPublicKey: effectiveSettings.paymob?.publicKey || '',
        paymobSecretKey: effectiveSettings.paymob?.secretKey || '',
        paymobCountry: effectiveSettings.paymob?.country || 'EG',
        dineropayMerchantKey: effectiveSettings.dineropay?.merchantKey || '',
        dineropayMerchantPassword: effectiveSettings.dineropay?.merchantPassword || '',
        nomodApiKey: effectiveSettings.nomod?.apiKey || ''

      })
      const paymobMethod = this.paymentMethods.find((method) => method.id === 'paymob')
      if (effectiveSettings.paymentMethod && effectiveSettings.paymentMethod.length > 0) {
        this.compareAndSetChecked(this.paymentMethods, effectiveSettings.paymentMethod)
        if (paymobMethod && effectiveSettings.paymob?.publicKey && effectiveSettings.paymob?.secretKey) {
          paymobMethod.checked = true
          this.updatePaymentState(paymobMethod, true)
        }
        this.paymentMethods.forEach((method) => {
          const dbMethod = effectiveSettings.paymentMethod!.find((pm: PaymentMethod) => pm.id === method.id)
          if (dbMethod && dbMethod.paymentId) {
            method.paymentId = dbMethod.paymentId
            const controlName = this.getControlName(method.id)
            this.paymentSettingsForm.patchValue({ [controlName]: method.paymentId })
          }
        })
      } else {
        this.paymentMethods.forEach((method) => {
          if (method.id === 'cod') {
            method.checked = true
            this.isCodChecked = true
            this.paymentData.push(method)
          }
        })
      }
      this.updatePaymobSubMethods()
    })
  }

  togglePayment(payment: PaymentMethod): void {
    // Stripe is configured through a popup instead of the inline expanded card. Once
    // an account already exists, clicking shows its live status instead of the KYC
    // form again — that form is only for first-time setup, before an account exists.
    if (payment.id === 'stripe') {
      const hasStartedOnboarding = !!this.stripeConnectStatus?.onboardingStatus
        && this.stripeConnectStatus.onboardingStatus !== 'not_started'
      if (hasStartedOnboarding) {
        this.openStripeConnectStatus(payment)
      } else {
        this.openStripeDialog(payment)
      }
      return
    }

    // Prevent unchecking if it's the last checked method
    if (!payment.checked && this.paymentData.length === 1 && this.paymentData[0].id === payment.id) {
      alert('You must have at least one payment method enabled.')
      return
    }

    payment.checked = !payment.checked
    this.updatePaymentState(payment, payment.checked)
    this.toggleValidators(payment, payment.checked)
    this.updatePaymobSubMethods()
  }

  openStripeDialog(payment: PaymentMethod): void {
    // Open immediately with a loading skeleton — fetching+decrypting previously saved
    // settings takes a moment, and this makes clicking Stripe feel instant instead of
    // dead air while the round trip is in flight.
    const dialogRef = this.dialog.open(StripeSettingsDialogComponent, {
      width: '560px',
      maxWidth: '90vw',
      panelClass: 'stripe-settings-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        stripApiKey: this.paymentSettingsForm.get('stripApiKey')?.value || '',
        stripePublishableKey: this.paymentSettingsForm.get('stripePublishableKey')?.value || '',
        stripeWebhookSecret: this.paymentSettingsForm.get('stripeWebhookSecret')?.value || '',
        isEnabled: payment.checked,
        isLoading: true
      }
    })

    this.loadStripeSettingsInto(dialogRef)

    dialogRef.afterClosed().subscribe((result: StripeSettingsDialogData | 'disable' | undefined) => {
      if (!result) return

      if (result === 'disable') {
        if (this.paymentData.length === 1 && this.paymentData[0].id === payment.id) {
          alert('You must have at least one payment method enabled.')
          return
        }
        payment.checked = false
        this.updatePaymentState(payment, false)
        this.toggleValidators(payment, false)
        this.updatePaymobSubMethods()
        return
      }

      this.paymentSettingsForm.patchValue(result)

      this.settingService.saveStripeConnectSettings(result).subscribe({
        error: (err) => {
          console.error('Failed to save Stripe settings', err)
          alert('Failed to save Stripe settings. Please try again.')
        }
      })

      // BYOK: no Connect account to create, no bank/ToS onboarding step — the key is
      // either valid or not (validateStripeKey, run from the main Save button below),
      // so it's enabled immediately, same as every other classic payment method.
      if (result.connectionMode === 'byok') {
        payment.checked = true
        this.updatePaymentState(payment, true)
        this.toggleValidators(payment, true)
        this.updatePaymobSubMethods()
        return
      }

      // Connect: not marked checked/enabled yet — the account this creates still needs
      // a bank account + ToS acceptance before it can actually receive transfers, so it
      // isn't "connected" just because the form was submitted. finalizeStripeConnectStatus()
      // below is what actually flips it on, and only once Stripe confirms it's ready.

      // Calls stacks-agents' existing Stripe Connect "create account" API so Save actually
      // provisions/syncs the project's real Stripe Connect account, not just the raw form fields above.
      this.settingService.createStripeConnectAccount(this.buildStripeConnectAccountPayload(result)).subscribe({
        next: () => this.openStripeConnectOnboarding(payment),
        error: (err) => {
          console.error('Failed to create Stripe Connect account', err)
          alert('Failed to set up your Stripe Connect account. Please check your details and try again.')
        }
      })
    })
  }

  /**
   * Shown instead of the KYC form once an account already exists — "Connected",
   * or "Action required" with a way to continue setup. The link at the bottom
   * is the only way back to the KYC form after this point.
   */
  openStripeConnectStatus(payment: PaymentMethod): void {
    const dialogRef = this.dialog.open(StripeConnectStatusComponent, {
      width: '420px',
      maxWidth: '90vw',
      panelClass: 'stripe-connect-status-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      autoFocus: false,
      data: {
        isFullyActive: this.isStripeConnectFullyActive,
        payoutsAtRisk: !!this.stripeConnectStatus?.payoutsAtRisk,
        eventuallyDue: this.stripeConnectStatus?.eventuallyDue ?? [],
        currentDeadline: this.stripeConnectStatus?.currentDeadline ?? null
      }
    })

    dialogRef.afterClosed().subscribe((action: 'continue' | 'settings' | undefined) => {
      if (action === 'continue') this.openStripeConnectOnboarding(payment)
      if (action === 'settings') this.openStripeDialog(payment)
    })
  }

  /**
   * Opens Stripe's embedded account-onboarding component (bank account + ToS
   * acceptance) for the project's already-created Connect account. Called right
   * after createStripeConnectAccount succeeds — the account exists at this point
   * but can't receive transfers yet without this step. Once the merchant closes
   * it (whether they finished or bailed out partway), re-checks the real status
   * rather than assuming completion just because the popup closed.
   */
  openStripeConnectOnboarding(payment: PaymentMethod): void {
    const dialogRef = this.dialog.open(StripeConnectOnboardingComponent, {
      width: '600px',
      maxWidth: '90vw',
      panelClass: 'stripe-connect-onboarding-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false
    })

    dialogRef.afterClosed().subscribe(() => this.finalizeStripeConnectStatus(payment))
  }

  /**
   * The single source of truth for whether Stripe.
   */
  private finalizeStripeConnectStatus(payment: PaymentMethod): void {
    this.settingService.refreshStripeConnectStatus().subscribe({
      next: (status: any) => {
        this.stripeConnectStatus = status
        if (this.isStripeConnectFullyActive) this.syncStripeCheckedState(payment)
      },
      error: (err) => console.error('Failed to refresh Stripe Connect status', err)
    })
  }

  /**
   * Maps the popup's validated form values into stacks-agents' CreateConnectAccountInput
   * shape, keeping only the fields Stripe actually requires for the selected
   * country + business type (per stripeConnectRequiredFields).
   */
  private buildStripeConnectAccountPayload(result: StripeSettingsDialogData): Omit<CreateConnectAccountInput, 'projectId'> {
    const isIndividual = result.businessType !== 'company'
    const required = new Set<string>((stripeConnectRequiredFields as any)[result.country || '']?.[result.businessType || 'individual'] ?? [])
    const pick = (obj: Record<string, string | undefined> = {}) =>
      Object.fromEntries(Object.entries(obj).filter(([key]) => required.has(key)))

    return {
      country: result.country || '',
      businessType: isIndividual ? 'individual' : 'company',
      email: this.loginService.getCurrentUser()?.email || '',
      individual: isIndividual ? pick(result.individual) : undefined,
      business: isIndividual ? undefined : pick(result.business)
    }
  }

  /**
   * Fetches previously saved Stripe settings in the background and patches them into
   * the already-open dialog, swapping its skeleton for the real fields.
   */
  private async loadStripeSettingsInto(dialogRef: MatDialogRef<StripeSettingsDialogComponent>): Promise<void> {
    let existingData: Partial<StripeSettingsDialogData> | null = null
    try {
      existingData = await this.settingService.getStripeConnectSettings().toPromise()
    } catch (err) {
      console.error('Failed to load existing Stripe settings', err)
    }
    dialogRef.componentInstance?.applyLoadedData(existingData)
  }

  updatePaymentId(payment: PaymentMethod, paymentId: string): void {
    payment.paymentId = paymentId
  }

  compareAndSetChecked(availableMethods: PaymentMethod[], selectedMethods: PaymentMethod[]): void {
    availableMethods.forEach((method) => {
      const isSelected = selectedMethods.some((selected) => selected.id === method.id)
      method.checked = isSelected
      this.updatePaymentState(method, isSelected)
    })
  }

  private updatePaymentState(payment: PaymentMethod, isChecked: boolean): void {
    switch (payment.id) {
      case 'stripe':
        this.isStripChecked = isChecked
        break
      case 'geidea':
        this.isGeideaChecked = isChecked
        break
      case 'paypal':
        this.isPaypalChecked = isChecked
        break
      case 'cod':
        this.isCodChecked = isChecked
        break
      case 'dineropay':
        this.isDineroPayChecked = isChecked
        break
      case 'nomod':
        this.isNomodChecked = isChecked
        break
      case 'paymob':
        this.isPaymobChecked = isChecked
        break
      case 'paymob-migs':
        this.isPaymobMigsChecked = isChecked
        break
      case 'paymob-wallet':
        this.isPaymobWalletChecked = isChecked
        break
      case 'paymob-paypal':
        this.isPaymobPaypalChecked = isChecked
        break
      case 'paymob-kiosk':
        this.isPaymobKioskChecked = isChecked
        break
      case 'paymob-applePay':
        this.isPaymobKioskChecked = isChecked
        break
    }

    // Only add to paymentData if it's not the paymob parent
    if (payment.id !== 'paymob') {
      const index = this.paymentData.findIndex((p) => p.id === payment.id)
      if (isChecked && index === -1) {
        // Remove paymentId for stripe and geidea before adding
        const paymentCopy = { ...payment }
        if (paymentCopy.id === 'stripe' || paymentCopy.id === 'geidea') {
          delete paymentCopy.paymentId
        }
        this.paymentData.push(paymentCopy)
      } else if (!isChecked && index !== -1) {
        this.paymentData.splice(index, 1)
      }
    }
  }

  private toggleValidators(payment: PaymentMethod, isChecked: boolean): void {
    const controlName = this.getControlName(payment.id)
    const control = this.paymentSettingsForm.get(controlName)
    if (control) {
      if (isChecked) {
        control.setValidators(Validators.required)
      } else {
        control.clearValidators()
      }
      control.updateValueAndValidity()
    }
    // DineroPay has a second required field (password)
    if (payment.id === 'dineropay') {
      const pwdControl = this.paymentSettingsForm.get('dineropayMerchantPassword')
      if (isChecked) {
        pwdControl?.setValidators(Validators.required)
      } else {
        pwdControl?.clearValidators()
      }
      pwdControl?.updateValueAndValidity()
    }
    if (payment.id === 'paymob') {
      const countryControl = this.paymentSettingsForm.get('paymobCountry')
      if (isChecked) {
        countryControl?.setValidators(Validators.required)
      } else {
        countryControl?.clearValidators()
      }
      countryControl?.updateValueAndValidity()
    }
  }

  getControlName(paymentId: string): string {
    const map: { [key: string]: string } = {
      stripe: 'stripApiKey',
      geidea: 'geideaPublicKey',
      paypal: 'paypalClientId',
      'paymob-migs': 'paymobMigsPaymentId',
      'paymob-wallet': 'paymobWalletPaymentId',
      'paymob-paypal': 'paymobPaypalPaymentId',
      'paymob-kiosk': 'paymobKioskPaymentId',
      'paymob-applePay':'paymobApplePaymentId',
      dineropay: 'dineropayMerchantKey',
      nomod: 'nomodApiKey'
    }
    return map[paymentId] || ''
  }

  arePaymobFieldsFilled(): boolean {
    const formValue = this.paymentSettingsForm.value
    return !!formValue.paymobPublicKey && !!formValue.paymobSecretKey
  }

  private updatePaymobSubMethods(): void {
    this.paymobSubMethods = this.paymentMethods.filter((m) => m.category === 'paymob' && m.id !== 'paymob')
  }

  async saveSettings() {
    this.formSubmitted = true
    this.settingsProgress = true
    this.saveState = true
    const formValue = this.paymentSettingsForm.value

    // Check if at least one payment method is selected
    if (this.paymentData.length === 0) {
      alert('You must enable at least one payment method.')
      this.settingsProgress = false
      this.saveState = false
      return
    }

    // Ensure all payment methods have their paymentId updated from the form (only for Paymob sub-methods)
    this.paymentMethods.forEach((method) => {
      const controlName = this.getControlName(method.id)
      if (formValue[controlName] && method.category === 'paymob' && method.id !== 'paymob') {
        method.paymentId = formValue[controlName]
        const paymentIndex = this.paymentData.findIndex((p) => p.id === method.id)
        if (paymentIndex !== -1) {
          this.paymentData[paymentIndex].paymentId = method.paymentId
        }
      }
    })

    this.settings = {
      ...this.settings,
      paymentMethod: this.paymentData,
      stripApiKey: this.isStripChecked ? formValue.stripApiKey : '',
      stripePublishableKey: this.isStripChecked ? formValue.stripePublishableKey : '',
      stripeWebhookSecret: this.isStripChecked ? formValue.stripeWebhookSecret : '',
      geidea: this.isGeideaChecked
        ? {
            publicKey: formValue.geideaPublicKey,
            apiPassword: formValue.geideaApiPassword,
            enCodedGeideaAuth: btoa(`${formValue.geideaPublicKey}:${formValue.geideaApiPassword}`)
          }
        : { publicKey: '', apiPassword: '', enCodedGeideaAuth: '' },

      paypal: this.isPaypalChecked
        ? {
            clientId: formValue.paypalClientId,
            secretKey: formValue.paypalSecretKey,
            paypalToken: btoa(`${formValue.paypalClientId}:${formValue.paypalSecretKey}`)
          } : { clientId: '', secretKey: '',paypalToken:''},
      ...(this.paymobSubMethods.some((m) => m.checked) && {
        paymob: {
          publicKey: formValue.paymobPublicKey,
          secretKey: formValue.paymobSecretKey,
          country: formValue.paymobCountry,
          baseUrl: this.paymobCountries.find(c => c.code === formValue.paymobCountry)?.api
        }
      }),
      dineropay: this.isDineroPayChecked
        ? {
            merchantKey: (formValue.dineropayMerchantKey ?? '').trim(),
            merchantPassword: (formValue.dineropayMerchantPassword ?? '').trim()
          }
        : { merchantKey: '', merchantPassword: '' },
      nomod: this.isNomodChecked
        ? { apiKey: (formValue.nomodApiKey ?? '').trim() }
        : { apiKey: '' }
    }

    // Only BYOK-mode Stripe has a raw secret key to validate here — Connect-mode
    // saving is just persisting the paymentMethod list + whatever the KYC dialog
    // already collected, with createStripeConnectAccount() doing the real work.
    if (this.isStripChecked && formValue.stripApiKey) {
      await this.validateStripeKey(formValue.stripApiKey)
      if (!this.isValidStripeApiKey) {
        alert(this.invalidStripeApiKey || 'Invalid Stripe secret key')
        this.settingsProgress = false
        this.saveState = false
        return
      }
    }

    if (this.isGeideaChecked && formValue.geideaPublicKey && formValue.geideaApiPassword) {
      const token = this.settings.geidea?.enCodedGeideaAuth
      if (token) {
        await this.validateGeidea(token)
      }
      if (!this.isValidGeidea) {
        this.settingsProgress = false
        this.saveState = false
        return
      }
    }
    
    /**
     * PayPal Validation Block
     * ------------------------------------------------------------
     * This section validates PayPal credentials before saving settings.
     * 
     * - Runs only if PayPal is selected (`isPaypalChecked`) and both
     *   `paypalClientId` and `paypalSecretKey` fields have values.
     * - Uses the saved `paypalToken` (Base64 encoded credentials) to
     *   request an OAuth2 token from PayPal via `validatePaypal()`.
     * - Awaits the validation result (`true` for valid credentials, `false` for invalid).
     * - If validation fails:
     *    → Stops the save process by returning early.
     *    → Disables progress (`settingsProgress = false`).
     *    → Keeps form error messages visible for the user.
     * - If validation passes:
     *    → Allows the save process to continue normally.
     */
  if (this.isPaypalChecked && formValue.paypalClientId && formValue.paypalSecretKey) {
        const token = this.settings.paypal?.paypalToken;
        if (token) {
        const isValid = await this.validatePaypal(token)
          if (!isValid) {
          this.settingsProgress = false
          this.saveState = false
          return
          }
        }
      }
    this.settingService.submitSettings(this.settings).subscribe(
      (result: any) => {
        this.settingsProgress = false
        this.saveState = false
        console.log('Settings saved successfully', result)
      },
      (error) => {
        console.error('Save failed', error)
        this.settingsProgress = false
        this.saveState = false
      }
    )
  }

  /** Validates the merchant's own Stripe secret key against api.stripe.com — BYOK mode only. */
  validateStripeKey(apiKey: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.settingService.validateStripeApiKey(apiKey).subscribe({
        next: () => {
          this.paymentSettingsForm.get('stripApiKey')?.setErrors(null)
          this.isValidStripeApiKey = true
          this.invalidStripeApiKey = ''
          resolve(true)
        },
        error: (err) => {
          const message = err?.error?.error?.message
          this.invalidStripeApiKey = message
          this.paymentSettingsForm.get('stripApiKey')?.setErrors({ invalidStripeApiKey: true })
          this.isValidStripeApiKey = false
          resolve(false)
        }
      })
    })
  }

  /**
   * Validates PayPal API credentials by requesting an OAuth2 access token
   * using the provided `token` (Base64 encoded clientId:secretKey).
   * Workflow:
   * - Calls `settingService.validatePaypalToken(token)` which sends a POST
   *   request to PayPal’s `/v1/oauth2/token` endpoint.
   * - If the response contains a valid `access_token`, the credentials are valid:
   *    → Clears validation errors on `paypalClientId` and `paypalSecretKey` fields.
   *    → Resets any previous invalid key message.
   *    → Resolves the Promise with `true`.
   * - If the response does **not** include an access token (unexpected format):
   *    → Marks both PayPal fields as invalid.
   *    → Resolves the Promise with `false`.
   * - If the HTTP request fails (e.g., 401 Unauthorized):
   *    → Captures PayPal’s error description.
   *    → Marks both fields as invalid.
   *    → Resolves the Promise with `false`.
   *
   * @param token The Base64 encoded PayPal client credentials (`clientId:secretKey`)
   * @returns A Promise that resolves to:
   *          - `true` if PayPal credentials are valid,
   *          - `false` if invalid or an error occurs.
   */
  validatePaypal(token: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.settingService.validatePaypalToken(token).subscribe({
        next: (response: any) => {
          if (response.access_token) {
            // Valid PayPal credentials
            this.paymentSettingsForm.get('paypalClientId')?.setErrors(null)
            this.paymentSettingsForm.get('paypalSecretKey')?.setErrors(null)
            this.isValidPaypal = true
            this.invalidPaypalKey = ''
            resolve(true)
          } else {
            // Unexpected response (no token returned)
            this.paymentSettingsForm.get('paypalClientId')?.setErrors({ invalidClientId: true })
            this.paymentSettingsForm.get('paypalSecretKey')?.setErrors({ invalidSecretKey: true })
            this.isValidPaypal = false
            resolve(false)
          }
        },
        error: (error: any) => {
          // Invalid credentials or network error
          this.invalidPaypalKey = error.error['error_description']
          this.paymentSettingsForm.get('paypalClientId')?.setErrors({ invalidClientId: true })
          this.paymentSettingsForm.get('paypalSecretKey')?.setErrors({ invalidSecretKey: true })
          this.isValidPaypal = false
          resolve(false)
        }
      })
    })
  }

  validateGeidea(token: string): Promise<boolean> {
    return new Promise((resolve) => {
    this.settingService.validateGeideaToken(token).subscribe({
      next: (response) => {
        if (response.detailedResponseCode === '000') {
          this.paymentSettingsForm.get('geideaPublicKey')?.setErrors(null)
          this.paymentSettingsForm.get('geideaApiPassword')?.setErrors(null)
          this.isValidGeidea = true
          this.invalidGeideaKey = ''
            resolve(true)
        } else {
          this.invalidGeideaKey = response.detailedResponseMessage
          this.paymentSettingsForm.get('geideaPublicKey')?.setErrors({ invalidPublicKey: true })
          this.paymentSettingsForm.get('geideaApiPassword')?.setErrors({ invalidPassword: true })
          this.isValidGeidea = false
            resolve(false)
        }
      },
      error: () => {
        this.invalidGeideaKey = 'Invalid Geidea credentials'
        this.paymentSettingsForm.get('geideaPublicKey')?.setErrors({ invalidPublicKey: true })
        this.paymentSettingsForm.get('geideaApiPassword')?.setErrors({ invalidPassword: true })
        this.isValidGeidea = false
          resolve(false)
      }
      })
    })
  }



  

  goBack(): void {
    window.history.back()
  }

  getPaymentIcon(paymentId: string, isChecked: boolean): string {
    const icons: { [key: string]: { checked: string; unchecked: string } } = {
      cod: {
        checked: 'assets/icons/COD.svg',
        unchecked: 'assets/icons/wallet.svg'
      },
      stripe: {
        checked: 'assets/icons/strip_checked.svg',
        unchecked: 'assets/icons/strip.svg'
      },
      geidea: {
        checked: 'assets/icons/geidea_checked.svg',
        unchecked: 'assets/icons/geidea.svg'
      },
      paypal: {
        checked: 'assets/icons/paypal_checked.svg',
        unchecked: 'assets/icons/paypal.svg'
      },
      dineropay: {
        checked: 'assets/icons/dineropay_checked.svg',
        unchecked: 'assets/icons/dineropay.svg'
      },
      nomod: {
        checked: 'assets/icons/nomed_checked.svg',
        unchecked: 'assets/icons/nomed.svg'
      },
      paymob: {
        checked: 'assets/icons/paymob_checked.svg',
        unchecked: 'assets/icons/paymob.svg'
      },
      'paymob-migs': {
        checked: 'assets/icons/paymob_checked.svg',
        unchecked: 'assets/icons/paymob.svg'
      },
      'paymob-wallet': {
        checked: 'assets/icons/paymob_checked.svg',
        unchecked: 'assets/icons/paymob.svg'
      },
      'paymob-paypal': {
        checked: 'assets/icons/paymob_checked.svg',
        unchecked: 'assets/icons/paymob.svg'
      },
      'paymob-kiosk': {
        checked: 'assets/icons/paymob_checked.svg',
        unchecked: 'assets/icons/paymob.svg'
      },
      'paymob-applePay': {
        checked: 'assets/icons/paymob_checked.svg',
        unchecked: 'assets/icons/paymob.svg'
      }
    }
    return isChecked ? icons[paymentId]?.checked || '' : icons[paymentId]?.unchecked || ''
  }
}
